/**
 * Who and how long, on top of a signed-in client's call (web-vrs.html frame
 * 2, approved 2026-10-29): "Pat Rivera · 4:12" in a small pill at the top
 * centre of the video, above the faces and never over the hands area. The
 * rules (callClock.ts) show a name only where the room knows it for sure, so
 * a VRS call shows the time alone.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { callClockStart, callPeerName, callTopText, formatCallClock } from '../../callClock';
import { readStoredActiveCall } from '../../storedCall';
const useStyles = makeStyles()(theme => {
    return {
        top: {
            alignItems: 'center',
            background: 'rgba(6, 7, 15, 0.66)',
            borderRadius: 16,
            boxSizing: 'border-box',
            color: theme.palette.text01,
            display: 'flex',
            fontSize: 15,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 600,
            left: '50%',
            maxWidth: 'calc(100% - 24px)',
            minHeight: 32,
            overflow: 'hidden',
            padding: '0 12px',
            pointerEvents: 'none',
            position: 'absolute',
            textOverflow: 'ellipsis',
            top: 8,
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
            zIndex: 6
        },
        // The held call's bar (HeldCallBar.web) has the top centre.
        belowHeldCall: {
            top: 60
        }
    };
});
const CallTopBar = ({ peerName, peerPresent, roomName }) => {
    const { classes, cx } = useStyles();
    const heldCall = useSelector((state) => Boolean(state['features/call-management']?.heldCall));
    const storedCall = readStoredActiveCall();
    const ringing = Boolean(roomName && storedCall?.outgoingRing
        && storedCall.roomName?.toLowerCase() === roomName.toLowerCase());
    const sawRing = useRef(false);
    const [answeredAt, setAnsweredAt] = useState();
    const [peerSeenAt, setPeerSeenAt] = useState();
    const [now, setNow] = useState(() => Date.now());
    // A ring this page saw end was the answer: the clock starts there.
    useEffect(() => {
        if (ringing) {
            sawRing.current = true;
        }
        else if (sawRing.current) {
            sawRing.current = false;
            setAnsweredAt(Date.now());
        }
    }, [ringing]);
    useEffect(() => {
        if (peerPresent && peerSeenAt === undefined) {
            setPeerSeenAt(Date.now());
        }
    }, [peerPresent, peerSeenAt]);
    const start = callClockStart({ answeredAt, peerSeenAt, roomName, storedCall });
    const running = start !== undefined;
    useEffect(() => {
        if (!running) {
            return undefined;
        }
        setNow(Date.now());
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tick);
    }, [running]);
    if (start === undefined) {
        return null;
    }
    return (React.createElement("div", { "aria-live": 'off', className: cx(classes.top, heldCall && classes.belowHeldCall), role: 'timer' }, callTopText(callPeerName({ peerName, roomName, storedCall }), formatCallClock(now - start))));
};
export default CallTopBar;
